import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  FormControl,
  Button,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyCase = {
  caseId: "",
  storeId: "",
  title: "",
  description: "",
  caseStatus: "",
  priority: "",
  assignedTo: "",
  contactId: "",
};

export default class CrmCases extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      cases: [],
      filteredCases: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentCase: { ...emptyCase },
    };
  }

  componentDidMount() {
    this.loadCases();
  }

  loadCases = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const cases = await fetchCrmList("Cases", "GetCases", storeId);
      const normalized = Array.isArray(cases) ? cases : [];
      this.setState({
        cases: normalized,
        filteredCases: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load cases.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredCases = this.state.cases.filter((crmCase) => {
      const title = (crmCase.title || crmCase.subject || "").toLowerCase();
      const status = (crmCase.caseStatus || crmCase.status || "").toLowerCase();
      const priority = (crmCase.priority || "").toLowerCase();
      return (
        title.includes(normalizedSearch) ||
        status.includes(normalizedSearch) ||
        priority.includes(normalizedSearch)
      );
    });

    this.setState({ search, filteredCases });
  };

  formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  handleShowModal = (crmCase = null) => {
    this.setState({
      currentCase: crmCase ? { ...crmCase } : { ...emptyCase },
      isEdit: Boolean(crmCase),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentCase: {
        ...prevState.currentCase,
        [field]: value,
      },
    }));
  };

  saveCase = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const crmCase = {
      ...this.state.currentCase,
      storeId,
      caseId:
        this.state.currentCase.caseId || "00000000-0000-0000-0000-000000000000",
    };

    try {
      await saveCrmItem("Cases", "AddOrUpdateCase", crmCase);
      await this.loadCases();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save case." });
    }
  };

  deleteCase = async (caseId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this case?")) return;

    try {
      await deleteCrmItem("Cases", "DeleteCase", storeId, caseId);
      await this.loadCases();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete case." });
    }
  };

  render() {
    const {
      filteredCases,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentCase,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Cases</h2>
            <p className="text-muted">
              Manage support cases for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Case
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadCases}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM cases.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by title, status, or priority"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredCases.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-5">
                  No cases found.
                </td>
              </tr>
            ) : (
              filteredCases.map((crmCase) => (
                <tr key={crmCase.caseId || Math.random()}>
                  <td>{crmCase.title || crmCase.subject || "-"}</td>
                  <td>{crmCase.caseStatus || crmCase.status || "-"}</td>
                  <td>{crmCase.priority || "-"}</td>
                  <td>
                    {this.formatDate(
                      crmCase.createdDateTime || crmCase.createdOn,
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(crmCase)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteCase(crmCase.caseId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? "Edit Case" : "Add Case"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="caseTitle">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={currentCase.title}
                      onChange={(e) =>
                        this.handleFieldChange("title", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="caseStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      value={currentCase.caseStatus}
                      onChange={(e) =>
                        this.handleFieldChange("caseStatus", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="casePriority">
                    <Form.Label>Priority</Form.Label>
                    <Form.Control
                      value={currentCase.priority}
                      onChange={(e) =>
                        this.handleFieldChange("priority", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="caseAssignedTo">
                    <Form.Label>Assigned To</Form.Label>
                    <Form.Control
                      value={currentCase.assignedTo}
                      onChange={(e) =>
                        this.handleFieldChange("assignedTo", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="caseContactId">
                    <Form.Label>Contact Id</Form.Label>
                    <Form.Control
                      value={currentCase.contactId}
                      onChange={(e) =>
                        this.handleFieldChange("contactId", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="caseDescription">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={currentCase.description}
                      onChange={(e) =>
                        this.handleFieldChange("description", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveCase}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
